import {
  ComponentPropsWithoutRef,
  MouseEventHandler,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
} from "react-relay";
import { generatePath } from "react-router";
import Suspense from "@/common/Suspense";
import {
  CalendarIcon,
  DotsVerticalIcon,
  Edit02Icon,
  GitBranch01Icon,
  Trash01Icon,
  XIcon,
} from "@/icons";
import {
  ResponsiveSheet,
  ResponsiveSheetContent,
  ResponsiveSheetPortal,
  ResponsiveSheetTrigger,
} from "@/kit/ui/responsive-sheet";
import {
  ResponsiveModalClose,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/kit/ui/responsive-modal";
import { Badge } from "@/kit/ui/badge";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import { Popover, PopoverContent, PopoverTrigger } from "@/kit/ui/popover";
import { Signal, useSignalCleanup, useSignalValue } from "@/utils/signal";
import { cn } from "@/utils/tailwind";
import LoadMore from "./LoadMore";
import WorkspacePublishButton from "./WorkspacePublishButton";
import WorkspaceVersionDeleteButton from "./WorkspaceVersionDeleteButton";
import WorkspaceVersionRenameButton from "./WorkspaceVersionRenameButton";
import { WorkspaceVersionHistoryFragment$key } from "./__generated__/WorkspaceVersionHistoryFragment.graphql";
import { WorkspaceVersionHistoryItemFragment$key } from "./__generated__/WorkspaceVersionHistoryItemFragment.graphql";
import WorkspaceVersionHistoryPaginationQuery, {
  WorkspaceVersionHistoryPaginationQuery as WorkspaceVersionHistoryPaginationQueryType,
} from "./__generated__/WorkspaceVersionHistoryPaginationQuery.graphql";
import { WorkspaceVersionHistoryPaginationFragment$key } from "./__generated__/WorkspaceVersionHistoryPaginationFragment.graphql";
import { splitWorkspaceSlug } from "@/utils/routing";

const Fragment = graphql`
  fragment WorkspaceVersionHistoryFragment on Workspace {
    id
    slug
    createdAt
    ...WorkspacePublishButtonFragment
  }
`;

const PaginationFragment = graphql`
  fragment WorkspaceVersionHistoryPaginationFragment on Workspace
  @refetchable(queryName: "WorkspaceVersionHistoryPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    versions(after: $cursor, first: $count)
      @connection(key: "WorkspaceVersionHistoryPaginationFragment_versions") {
      edges {
        node {
          id
          ...WorkspaceVersionHistoryItemFragment
        }
      }
    }
  }
`;

const ItemFragment = graphql`
  fragment WorkspaceVersionHistoryItemFragment on WorkspaceVersion {
    version
    createdAt
    publishedAt
    workspace {
      slug
    }
    viewerCanDelete: viewerCan(action: DELETE_WORKSPACE_VERSION)
    viewerCanUpdate: viewerCan(action: UPDATE_WORKSPACE_VERSION)

    ...WorkspaceVersionDeleteButtonFragment
    ...WorkspaceVersionRenameButtonFragment
  }
`;

interface VersionHistoryContext {
  open: Signal<boolean>;
  currentVersion?: string | null | undefined;
}
const VersionHistoryContext = createContext<VersionHistoryContext | null>(null);

export interface WorkspaceVersionHistoryProps extends PropsWithChildren {
  workspace: WorkspaceVersionHistoryFragment$key;
  currentVersion?: string | null | undefined;
}

export default function WorkspaceVersionHistory({
  workspace: workspaceFragment,
  currentVersion,
  children,
}: WorkspaceVersionHistoryProps) {
  const workspace = useFragment(Fragment, workspaceFragment);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const open = useMemo(() => new Signal(false), [workspace.id, currentVersion]);
  useSignalCleanup(open);

  const isOpen = useSignalValue(open);

  const context = useMemo<VersionHistoryContext>(
    () => ({
      open,
      currentVersion,
    }),
    [open, currentVersion],
  );

  return (
    <ResponsiveSheet
      open={isOpen}
      onOpenChange={(open) => context.open.dispatch(open)}
    >
      {children && (
        <ResponsiveSheetTrigger asChild>{children}</ResponsiveSheetTrigger>
      )}

      <ResponsiveSheetPortal>
        <ResponsiveSheetContent>
          <ResponsiveModalHeader className="flex flex-col justify-between items-center sm:p-6  px-4 pt-4 pb-5 border-b border-t-grey-200">
            <div className="w-full flex flex-row items-center justify-between">
              <ResponsiveModalTitle>
                <p className="text-xl font-semibold text-grey-900">
                  <FormattedMessage defaultMessage="Version history" />
                </p>
              </ResponsiveModalTitle>
              <ResponsiveModalClose
                className="sm:flex hidden items-center"
                asChild
              >
                <Button variant="tertiary" size="md" hue="grey" icon>
                  <XIcon className="w-5 h-5" />
                </Button>
              </ResponsiveModalClose>
            </div>
          </ResponsiveModalHeader>

          <VersionHistoryContext.Provider value={context}>
            <Suspense>
              <VersionHistorySheet workspace={workspaceFragment} />
            </Suspense>
          </VersionHistoryContext.Provider>

          <ResponsiveModalFooter className="flex flex-col justify-center items-end sm:p-6 px-4 pt-4 pb-8 border-t border-t-grey-200">
            <WorkspacePublishButton workspace={workspace} />
          </ResponsiveModalFooter>
        </ResponsiveSheetContent>
      </ResponsiveSheetPortal>
    </ResponsiveSheet>
  );
}

interface VersionHistorySheetProps {
  workspace: WorkspaceVersionHistoryFragment$key;
}

function VersionHistorySheet(props: VersionHistorySheetProps) {
  const workspace = useFragment(Fragment, props.workspace);
  const query = useLazyLoadQuery<WorkspaceVersionHistoryPaginationQueryType>(
    WorkspaceVersionHistoryPaginationQuery,
    { id: workspace.id },
  );
  const {
    data: { versions },
    isLoadingNext,
    hasNext,
    loadNext,
  } = usePaginationFragment(
    PaginationFragment,
    query.node as WorkspaceVersionHistoryPaginationFragment$key,
  );

  if (versions.edges.length <= 0) {
    return (
      <div className="w-full flex flex-col items-center justify-center gap-3 bg-grey-50 rounded-xl p-8">
        <GitBranch01Icon className="h-8 w-8 text-grey-700" />
        <div className="flex flex-col items-center justify-center gap-1">
          <p className="text-md font-semibold text-grey-950">
            <FormattedMessage defaultMessage="No versions yet" />
          </p>
          <p className="text-sm font-normal text-grey-700">
            <FormattedMessage defaultMessage="Create a version bellow" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-4 px-6 pt-4 pb-6 overflow-y-auto">
        {versions.edges.map(({ node }) => (
          <VersionItem key={node.id} version={node} />
        ))}
      </ul>

      <LoadMore
        hasMore={hasNext}
        isLoading={isLoadingNext}
        loadMore={loadNext}
      />
    </>
  );
}

function VersionItem(props: {
  version: WorkspaceVersionHistoryItemFragment$key;
}) {
  const version = useFragment(ItemFragment, props.version);
  const context = useContext(VersionHistoryContext);
  const isCurrent = context?.currentVersion === version.version;

  const onClick: MouseEventHandler = () => {
    context?.open.dispatch(false);
  };

  return (
    <Item active={isCurrent}>
      <div className="flex flex-col items-start gap-2">
        <div className="flex flex-row items-center gap-2">
          <Link
            to={generatePath("/:owner/:slug/versions/:version", {
              ...splitWorkspaceSlug(version.workspace.slug),
              version: version.version,
            })}
            hue="grey"
            className="hover:text-brand-500"
            onClick={onClick}
          >
            <FormattedMessage
              defaultMessage="Version {version}"
              values={{ version: version.version }}
            />
          </Link>
          {!version.publishedAt && (
            <Badge className="gap-1">
              <FormattedMessage defaultMessage="Draft" />
            </Badge>
          )}
        </div>
        <div className="flex items-center flex-row">
          <div className="flex flex-row items-center gap-2 text-grey-500">
            <CalendarIcon className="w-5 h-5" />
            <p className="text-sm font-medium text-grey-600">
              <FormattedMessage
                defaultMessage="Created {date}"
                values={{
                  date: (
                    <FormattedDate
                      value={version.createdAt}
                      month="short"
                      day="numeric"
                      year="numeric"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>
      </div>

      <VersionItemMenu version={props.version} />
    </Item>
  );
}

function VersionItemMenu(props: {
  version: WorkspaceVersionHistoryItemFragment$key;
}) {
  const { viewerCanDelete, viewerCanUpdate, publishedAt, ...version } =
    useFragment(ItemFragment, props.version);

  const context = useContext(VersionHistoryContext);
  const isCurrentVersion = context?.currentVersion === version.version;

  // Only unpublished drafts can be renamed.
  const canRename = viewerCanUpdate && !publishedAt;

  if (!viewerCanDelete && !canRename) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="tertiary" hue="grey" size="md" icon>
          <DotsVerticalIcon className="w-5 h-5" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 w-48 divide-y"
        align="end"
        alignOffset={10}
      >
        {canRename && (
          <WorkspaceVersionRenameButton
            version={version}
            variant="tertiary"
            hue="grey"
            size="md"
            className="flex flex-row items-center justify-start gap-2 w-full hover:bg-linear-to-r from-[#F2F4F7] to-[#FFF] rounded-none active:bg-none bg-none px-3 py-4"
          >
            <Edit02Icon className="w-6 h-6" />
            <FormattedMessage defaultMessage="Rename version" />
          </WorkspaceVersionRenameButton>
        )}
        {viewerCanDelete && (
          <WorkspaceVersionDeleteButton
            version={version}
            hasCurrentFocus={isCurrentVersion}
            variant="tertiary"
            hue="grey"
            size="md"
            className="flex flex-row items-center justify-start gap-2 w-full hover:bg-linear-to-r from-[#FFF1F0] to-[#FFF] rounded-none active:bg-none bg-none px-3 py-4"
          >
            <Trash01Icon className="w-6 h-6" />
            <FormattedMessage defaultMessage="Delete version" />
          </WorkspaceVersionDeleteButton>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ItemProps extends ComponentPropsWithoutRef<"li"> {
  active?: boolean | undefined;
}

function Item({ active, className, ...props }: ItemProps) {
  return (
    <li
      {...props}
      className={cn(
        "p-4 flex flex-row justify-between sm:items-center items-baseline rounded-lg transition-colors",
        active
          ? "border-2 border-brand-400 bg-linear-to-r from-brand-50 to-white"
          : "border border-grey-200 hover:bg-linear-to-r hover:from-[#F8F7FC] hover:to-white",
        className,
      )}
    />
  );
}
